import { customElement, query, property } from 'lit/decorators.js';
import { FormFieldInput } from '../../gzForm/formField/component/FormFieldInput';
import { css, html } from 'lit';
import { calculateNewMarkerCounts, constructFormattedText, extractFormatting, getListTypes } from './helpers';
import { HEADING_REGEX } from './constants';

/**
 * @summary This component is used within the gz-markdown-editor component, as a textarea for editing markdown content.
 * @tag gz-markdown-editor-textarea
 * @fires change - Custom event dispatched when the textarea content changes.
 * @fires select - Custom event dispatched when text is selected within the textarea.
 * @fires input - Custom event dispatched whenever the textarea input value changes.
 * @fires selection-cleared - Custom event dispatched when the textarea selection is cleared.
 */
@customElement('gz-markdown-editor-textarea')
export class GzMarkdownEditorTextareaWebComponent extends FormFieldInput<string> {
  static get styles() {
    return css`
      *::-webkit-scrollbar {
        width: 6px;
        height: 6px;
      }

      * {
        scrollbar-width: thin;
        scrollbar-color: var(--chart-104-pressed) var(--white-layout-302-level-1-elevation);
      }

      *::-webkit-scrollbar-track {
        background: var(--white-layout-302-level-1-elevation);
      }
      *::-webkit-scrollbar-thumb {
        background-color: var(--chart-104-pressed);
        border-radius: 6px;
      }
      textarea {
        width: 100%;
        height: 100%;
        border: none;
        outline: none;
        resize: none;
        overflow: auto;
        font-family: var(--font-family-secondary);
        font-size: var(--body-size-medium);
        background-color: transparent;
        color: var(--main-titles-inputs-600);
        :focus {
          outline: none;
          border: none;
        }
      }
    `;
  }

  @query('textarea')
  private textarea!: HTMLTextAreaElement;

  /**
   * The markdown content of the textarea.
   */
  @property({ type: String, attribute: true, reflect: true })
  value = '';

  /**
   * Indicates whether the textarea has been modified.
   */
  @property({ type: Boolean, attribute: true, reflect: true })
  dirty = false;

  /**
   * The selection start index of the textarea before the last mouse down event.
   * This property is updated in the onMouseDown method to capture the current selection range when the user presses the mouse button.
   * @private
   */
  private _preSelectionStart = 0;
  /**
   * The selection end index of the textarea before the last mouse down event.
   * This property is updated in the onMouseDown method to capture the current selection range when the user presses the mouse button.
   * @private
   */
  private _preSelectionEnd = 0;

  /**
   * Returns the current selection start index of the textarea.
   */
  public get selectionStart(): number {
    return this.textarea.selectionStart;
  }

  /**
   * Returns the current selection end index of the textarea.
   */
  public get selectionEnd(): number {
    return this.textarea.selectionEnd;
  }

  public applyListFormatting(listType: 'bullet' | 'ordered'): void {
    if (!this.getSelectedText()) {
      return;
    }

    const { selectionStart, selectionEnd, value } = this.textarea;
    const selectedText = value.substring(selectionStart, selectionEnd);

    const { isBulletList, isOrderedList } = getListTypes(selectedText);

    // Split the selected text into individual lines
    const lines = selectedText.split('\n');

    // If the current list type matches the applied list type, remove formatting
    if ((listType === 'bullet' && isBulletList) || (listType === 'ordered' && isOrderedList)) {
      const unformattedLines = lines.map(line => line.replace(/^[-*]\s*/, '').replace(/^\d+\.\s*/, ''));
      this.setRangeSelection(unformattedLines.join('\n'), selectionStart, selectionEnd, 'end');
      return;
    }

    // Else, apply the requested list formatting
    const formattedLines = lines.map((line, index) => {
      // Strip existing list formatting (if present)
      line = line.replace(/^[-*]\s*/, '').replace(/^\d+\.\s*/, '');

      // Apply new list formatting
      if (listType === 'bullet') {
        return `- ${line}`;
      } else if (listType === 'ordered') {
        return `${index + 1}. ${line}`;
      }
      return line;
    });

    this.setRangeSelection(formattedLines.join('\n'), selectionStart, selectionEnd, 'end');
  }

  getFormValue(): string {
    if (this.textarea) {
      return this.textarea?.value;
    } else {
      return '';
    }
  }

  setFormValue(value: string): void {
    if (this.textarea) {
      this.textarea.value = value;
      this.dirty = false;
      this.dispatchEvent(new Event('input'));
    }
  }

  private onInput(): void {
    this.value = this.textarea.value;
    this.dirty = true;
    this.dispatchEvent(
      new CustomEvent('change', {
        bubbles: true,
      })
    );
  }

  private onTextSelect(): void {
    this.dispatchEvent(
      new CustomEvent('select', {
        detail: {
          text: this.getSelectedText(),
        },
        bubbles: true,
      })
    );
  }

  private onMouseDown(): void {
    this._preSelectionStart = this.textarea.selectionStart;
    this._preSelectionEnd = this.textarea.selectionEnd;
  }

  private onMouseUp(): void {
    // Use setTimeout to allow the browser to update the selection before checking
    setTimeout(() => {
      const currentStart = this.textarea.selectionStart;
      const currentEnd = this.textarea.selectionEnd;

      // If the selection hasn't changed, clear it
      if (currentStart === this._preSelectionStart && currentEnd === this._preSelectionEnd) {
        this.clearSelection();
      } else {
        // If the user clicked inside the textarea without performing a new selection, clear the selection
        if (currentStart === currentEnd) {
          this.clearSelection();
        }
      }
    }, 0);
  }

  private clearSelection(): void {
    const pos = this.textarea.selectionStart;
    this.textarea.setSelectionRange(pos, pos);
    this.value = this.textarea.value;
    this.dirty = true;
    this.dispatchEvent(new CustomEvent('selection-cleared'));
  }

  public getSelectedText(): string {
    return this.textarea.value.slice(this.textarea.selectionStart, this.textarea.selectionEnd);
  }

  public setRangeSelection(replacement: string, start: number, end: number, selectionMode?: SelectionMode): void {
    this.textarea.setRangeText(replacement, start, end, selectionMode);
    this.value = this.textarea.value;
    this.dispatchEvent(new Event('input'));
  }

  public toggleFormatting(marker: string): void {
    const { value, selectionStart, selectionEnd } = this.textarea;
    const selectedText = value.slice(selectionStart, selectionEnd);

    if (!selectedText) {
      return;
    }

    // Extract the number of leading and trailing markers
    const { leadingCount, trailingCount, unformattedText } = extractFormatting(selectedText);

    // Calculate new marker counts
    const { newLeadingCount, newTrailingCount } = calculateNewMarkerCounts(marker, leadingCount, trailingCount);

    // Construct the new formatted text
    const newText = constructFormattedText(unformattedText, newLeadingCount, newTrailingCount);

    // Replace the selected text with the newly formatted/unformatted text
    this.setRangeSelection(newText, selectionStart, selectionEnd, 'end');
  }

  public applyHeadingFormat(marker: string, selectedText: string): void {
    if (!selectedText) {
      return;
    }

    const { selectionStart, selectionEnd } = this.textarea;

    // Define a regex to match existing heading markers
    let unformattedText = selectedText;

    // Check if the selected text starts with any heading markers
    const match = selectedText.match(HEADING_REGEX);
    if (match) {
      // Remove the existing heading markers
      unformattedText = selectedText.replace(HEADING_REGEX, '');
    }

    let formattedText = '';

    if (marker === '0') {
      formattedText = `${unformattedText}`;
    } else {
      const markers = '#'.repeat(+marker);
      formattedText = `${markers} ${unformattedText}`;
    }

    // Replace the selected text with the formatted heading
    this.setRangeSelection(formattedText, selectionStart, selectionEnd, 'end');
  }

  public setMarkdownDefaultContent(initialContent: string): void {
    this.setFormValue(initialContent);
  }

  render() {
    return html`
      <textarea
        .value="${this.value}"
        @input="${this.onInput}"
        @select="${this.onTextSelect}"
        @mouseup="${this.onMouseUp}"
        @mousedown="${this.onMouseDown}"
      ></textarea>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-markdown-editor-textarea': GzMarkdownEditorTextareaWebComponent;
  }
}
